// vg-binding: P7.D-30
//
// apps/vendor/src/features/inventory/MovementsTab.tsx
//
// Phase 7 Wave 15 polish — vendor inventory Movements tab with filter bar
// + CSV export.

import { useCallback, useMemo, useState } from 'react';

import { Pagination } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  downloadMovementsCsv,
  useMovements,
  useStockList,
  type MovementType,
  type MovementsParams,
} from './api.js';

const PAGE_LIMIT = 20;

const TYPE_OPTIONS: Array<{ value: '' | MovementType; labelKey: string }> = [
  { value: '', labelKey: 'movements.type.all' },
  { value: 'adjust_increase', labelKey: 'movements.type.adjustIncrease' },
  { value: 'adjust_decrease', labelKey: 'movements.type.adjustDecrease' },
  { value: 'transfer_in', labelKey: 'movements.type.transferIn' },
  { value: 'transfer_out', labelKey: 'movements.type.transferOut' },
  { value: 'reserve', labelKey: 'movements.type.reserve' },
  { value: 'unreserve', labelKey: 'movements.type.unreserve' },
  { value: 'deduct_assigned', labelKey: 'movements.type.deductAssigned' },
  { value: 'restock_cancelled', labelKey: 'movements.type.restockCancelled' },
];

function shortId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { hour12: false });
}

function dateInputToMs(s: string, endOfDay = false): number | undefined {
  if (!s) return undefined;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return undefined;
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export function MovementsTab(): JSX.Element {
  const { t } = useTranslation('inventory');
  const [typeFilter, setTypeFilter] = useState<'' | MovementType>('');
  const [fromStr, setFromStr] = useState('');
  const [toStr, setToStr] = useState('');
  const [variantSearch, setVariantSearch] = useState('');
  // R3-5 (2026-05-16): typeahead source = stock list (limit 200) so user
  // picks a known variant by name instead of typing a 24-hex id.
  const [variantInput, setVariantInput] = useState('');
  const [showVariantDropdown, setShowVariantDropdown] = useState(false);
  const stockListQ = useStockList({ limit: 200 });
  const variantOptions = useMemo(() => {
    const q = variantInput.trim().toLowerCase();
    const rows = stockListQ.data?.rows ?? [];
    // Dedupe by variant_id.
    const seen = new Set<string>();
    const filtered: typeof rows = [];
    for (const r of rows) {
      if (seen.has(r.variant_id)) continue;
      seen.add(r.variant_id);
      if (q.length === 0) {
        filtered.push(r);
        continue;
      }
      const hay = [
        r.product_name,
        r.variant_label,
        r.variant_sku,
        r.variant_id,
      ]
        .filter((s): s is string => typeof s === 'string')
        .join(' ')
        .toLowerCase();
      if (hay.includes(q)) filtered.push(r);
    }
    return filtered.slice(0, 30);
  }, [variantInput, stockListQ.data?.rows]);
  const selectedVariantRow = useMemo(
    () =>
      stockListQ.data?.rows.find((r) => r.variant_id === variantSearch) ?? null,
    [stockListQ.data?.rows, variantSearch],
  );
  const [page, setPage] = useState(1);
  const [exportError, setExportError] = useState<string | null>(null);

  const baseFilters = useMemo<MovementsParams>(() => {
    const out: MovementsParams = { limit: PAGE_LIMIT };
    if (typeFilter) out.type = typeFilter;
    const from = dateInputToMs(fromStr, false);
    const to = dateInputToMs(toStr, true);
    if (typeof from === 'number') out.from_ms = from;
    if (typeof to === 'number') out.to_ms = to;
    if (variantSearch.trim().length === 24) {
      out.variant_id = variantSearch.trim();
    }
    return out;
  }, [typeFilter, fromStr, toStr, variantSearch]);

  const query = useMovements({ ...baseFilters, page });
  const rows = query.data?.rows ?? [];

  const resetCursor = useCallback(() => {
    setPage(1);
  }, []);

  async function handleExport(): Promise<void> {
    setExportError(null);
    try {
      await downloadMovementsCsv(baseFilters);
    } catch (err) {
      setExportError((err as Error).message);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        data-testid="inventory-movements-filter-bar"
        className="flex flex-wrap items-end gap-3 rounded-xl border border-pw-border bg-pw-surface p-3"
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-pw-text-muted">{t('movements.filter.fromDate')}</span>
          <input
            type="date"
            value={fromStr}
            onChange={(e) => {
              setFromStr(e.target.value);
              resetCursor();
            }}
            data-testid="inventory-movements-from"
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-pw-text-muted">{t('movements.filter.toDate')}</span>
          <input
            type="date"
            value={toStr}
            onChange={(e) => {
              setToStr(e.target.value);
              resetCursor();
            }}
            data-testid="inventory-movements-to"
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-pw-text-muted">{t('movements.filter.type')}</span>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as '' | MovementType);
              resetCursor();
            }}
            data-testid="inventory-movements-type"
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <div className="relative flex flex-col gap-1">
          <span className="text-xs font-medium text-pw-text-muted">{t('movements.filter.variantSearch')}</span>
          <input
            type="search"
            value={
              selectedVariantRow && !showVariantDropdown
                ? `${selectedVariantRow.product_name ?? selectedVariantRow.variant_id} · ${selectedVariantRow.variant_label ?? '—'}`
                : variantInput
            }
            placeholder={t('movements.filter.variantSearchPlaceholder')}
            onFocus={() => setShowVariantDropdown(true)}
            onChange={(e) => {
              setVariantInput(e.target.value);
              setShowVariantDropdown(true);
              if (variantSearch) {
                setVariantSearch('');
                resetCursor();
              }
            }}
            data-testid="inventory-movements-variant-search"
            className="h-9 w-64 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          />
          {showVariantDropdown ? (
            <div
              role="listbox"
              className="absolute top-full z-10 mt-1 max-h-56 w-72 overflow-y-auto rounded-md border border-pw-border bg-pw-bg shadow-lg"
            >
              <button
                type="button"
                onClick={() => {
                  setVariantSearch('');
                  setVariantInput('');
                  setShowVariantDropdown(false);
                  resetCursor();
                }}
                className="w-full px-3 py-2 text-left text-xs italic text-pw-text-muted hover:bg-pw-surface"
              >
                {t('movements.filter.clearVariant')}
              </button>
              {variantOptions.length === 0 ? (
                <p className="px-3 py-2 text-xs text-pw-text-muted">
                  {t('movements.filter.noMatch')}
                </p>
              ) : (
                variantOptions.map((r) => (
                  <button
                    key={r.variant_id}
                    type="button"
                    onClick={() => {
                      setVariantSearch(r.variant_id);
                      setShowVariantDropdown(false);
                      setVariantInput('');
                      resetCursor();
                    }}
                    className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-pw-surface"
                  >
                    <span className="font-medium text-pw-text">
                      {r.product_name ?? r.variant_id.slice(-8)}
                      {r.variant_sku ? (
                        <span className="ml-1 text-xs text-pw-text-muted">
                          ({r.variant_sku})
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-pw-text-muted">
                      {r.variant_label ?? '—'}
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          data-testid="inventory-movements-export-csv"
          onClick={() => void handleExport()}
          className="h-9 rounded-md border border-pw-border bg-pw-bg px-4 text-sm font-medium text-pw-text hover:bg-pw-surface"
        >
          {t('movements.filter.exportCsv')}
        </button>
      </div>

      {exportError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200">
          {exportError}
        </p>
      ) : null}

      {query.isLoading && rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-pw-text-muted">{t('movements.loading')}</p>
      ) : null}

      {!query.isLoading && rows.length === 0 ? (
        <div
          data-testid="inventory-movements-empty"
          className="rounded-xl border border-dashed border-pw-border bg-pw-surface px-6 py-10 text-center"
        >
          <p className="text-sm text-pw-text">{t('movements.emptyTitle')}</p>
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('movements.emptyHint')}
          </p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
          <div className="overflow-x-auto">
          <table
            data-testid="inventory-movements-table"
            className="w-full border-collapse text-sm"
          >
            <thead className="bg-pw-surface text-left text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-4 py-2">{t('movements.headerTime')}</th>
                <th className="px-4 py-2">{t('movements.headerProduct')}</th>
                <th className="px-4 py-2">{t('movements.headerVariant')}</th>
                <th className="px-4 py-2">{t('movements.headerWarehouse')}</th>
                <th className="px-4 py-2">{t('movements.headerType')}</th>
                <th className="px-4 py-2 text-right">{t('movements.headerQtyDelta')}</th>
                <th className="px-4 py-2">{t('movements.headerReason')}</th>
                <th className="px-4 py-2">{t('movements.headerReference')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.movement_id}
                  data-testid={`inventory-movement-row-${r.movement_id}`}
                  className="border-t border-pw-border hover:bg-pw-surface"
                >
                  <td className="px-4 py-2 text-pw-text-muted">{fmtDate(r.occurred_at)}</td>
                  <td className="px-4 py-2 text-pw-text">
                    <span className="font-medium">
                      {r.product_name ?? shortId(r.variant_id)}
                    </span>
                    {r.variant_sku ? (
                      <span className="ml-1 text-xs text-pw-text-muted">({r.variant_sku})</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.variant_label ?? shortId(r.variant_id)}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.warehouse_name ?? shortId(r.warehouse_id)}
                  </td>
                  <td className="px-4 py-2 text-pw-text">{r.type}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-pw-text">
                    {r.after.on_hand - r.before.on_hand >= 0 ? '+' : ''}
                    {r.after.on_hand - r.before.on_hand}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">{r.reason ?? '—'}</td>
                  <td className="px-4 py-2 text-pw-text-muted">{shortId(r.reference)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : null}

      <Pagination
        currentPage={page}
        pageCount={query.data?.page_count ?? 0}
        totalRows={query.data?.total ?? 0}
        testId="inventory-movements-pagination"
        disabled={query.isFetching}
        onChange={setPage}
      />
    </div>
  );
}

export default MovementsTab;
